import { displayToast } from "../../../components/app/AppToast";
import { AnalyticsItemList } from "../../../tools/analytics/analytics.types";
import { gaEventContentShare } from "../../../tools/analytics/domains/content.analytics";
import {
  convertInfographicToItemData,
  gaEventInfographicShare,
} from "../../../tools/analytics/domains/infographic.analytics";
import { useOnboardingGuard } from "../../user/hooks/useOnboardingGuard";
import { useTranslation } from "react-i18next";
import type { CurrentUser } from "../../user/hooks/useCurrentUser";
import { apiClient, type Api } from "../../../services/api";
import { shareUrl } from "../../app/utils/shareUrl";
import { REDIRECT_HOST } from "../../../config";

type InfographicDetail = Extract<
  Api["get"]["/webapp/public/content/:id"]["output"]["body"],
  { content_format: "Infographic" }
>;
type InfographicListItem = Extract<
  Api["get"]["/webapp/public/content"]["output"]["body"]["docs"][number],
  { content_format: "Infographic" }
>;
type ShareInfographicData = InfographicDetail | InfographicListItem;

export type UseShareInfographic = {
  triggerShare: (
    infographic: ShareInfographicData,
    user: CurrentUser,
    /** The room it is shared from, or null. See `useShareVideo`. */
    roomId: string | null
  ) => Promise<void>;
};

function publisherName(infographic: ShareInfographicData): string | null {
  if (infographic.company !== null) return infographic.company.name;
  if (infographic.congress !== null) return infographic.congress.name;
  return null;
}

export function useShareInfographic(
  analyticsListName: AnalyticsItemList
): UseShareInfographic {
  const { t } = useTranslation();
  const onboardingGuard = useOnboardingGuard();

  const triggerShare = async (
    infographic: ShareInfographicData,
    currentUser: CurrentUser,
    roomId: string | null
  ): Promise<void> => {
    try {
      if (
        !onboardingGuard(
          t("🔒 Please complete your profile first to share this content. ")
        )
      ) {
        return;
      }

      const path =
        roomId === null
          ? `/infographic/${infographic.slug}`
          : `/room/infographic/${roomId}/${infographic.slug}`;
      const longUrl = `${globalThis.location.origin}${path}?shared=${currentUser.id}`;
      // Not awaited: `shareUrl` must start inside the click (see there).
      const link = apiClient
        .post("/webapp/private/content/:id/shares", {
          param: { id: infographic.id },
        })
        .then(() =>
          apiClient
            .post("/webapp/private/share-links", {
              json: { type: "infographic", slug: infographic.slug, roomId },
            })
            .then(({ body }) => `https://${REDIRECT_HOST}/${body}`)
            .catch(() => longUrl)
        );
      const publisher = publisherName(infographic);
      const text =
        publisher === null
          ? t(
              "I think you might be interested in this infographic: {{title}}.",
              { title: infographic.title }
            )
          : t(
              "I think you might be interested in this infographic: {{title}} by {{publisherName}}.",
              { title: infographic.title, publisherName: publisher }
            );

      await shareUrl(infographic.title, text, link);

      gaEventInfographicShare({
        infographic,
        listName: analyticsListName,
      });
      gaEventContentShare(
        convertInfographicToItemData({
          infographic,
          listName: analyticsListName,
        })
      );
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  };

  return { triggerShare };
}
